import { useEffect, useRef, useState } from 'react';
import { useOnlineStatus } from '@/shared/hooks/useOnlineStatus';
import { useSyncStatus } from '@/shared/hooks/useSyncStatus';
import { WifiOff, Loader2, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

type BannerState = 'offline' | 'syncing' | 'error' | 'success' | 'idle';

const SUCCESS_DISPLAY_MS = 3000;

export function OfflineIndicator() {
  const isOnline = useOnlineStatus();
  const { isSyncing, lastSyncAt, syncError, syncNow } = useSyncStatus();
  const [showSuccess, setShowSuccess] = useState(false);
  const prevLastSyncAt = useRef<Date | null>(null);
  const successTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (
      lastSyncAt &&
      prevLastSyncAt.current?.getTime() !== lastSyncAt.getTime()
    ) {
      prevLastSyncAt.current = lastSyncAt;
      setShowSuccess(true);
      if (successTimerRef.current) clearTimeout(successTimerRef.current);
      successTimerRef.current = setTimeout(() => setShowSuccess(false), SUCCESS_DISPLAY_MS);
    }
    return () => {
      if (successTimerRef.current) clearTimeout(successTimerRef.current);
    };
  }, [lastSyncAt]);

  const state: BannerState = !isOnline
    ? 'offline'
    : isSyncing
    ? 'syncing'
    : syncError
    ? 'error'
    : showSuccess
    ? 'success'
    : 'idle';

  const banners: Record<Exclude<BannerState, 'idle'>, React.ReactNode> = {
    offline: (
      <div className="flex items-center justify-center gap-2 py-2 bg-destructive text-destructive-foreground text-sm font-medium">
        <WifiOff className="w-4 h-4 shrink-0" />
        <span>Mode hors ligne — données sauvegardées localement</span>
      </div>
    ),
    syncing: (
      <div className="flex items-center justify-center gap-2 py-2 bg-blue-500/90 text-white text-sm font-medium">
        <Loader2 className="w-4 h-4 shrink-0 animate-spin" />
        <span>Synchronisation en cours…</span>
      </div>
    ),
    error: (
      <div className="flex items-center justify-center gap-2 py-2 bg-orange-500/90 text-white text-sm font-medium">
        <AlertTriangle className="w-4 h-4 shrink-0" />
        <span>{syncError ?? 'Sync échouée'}</span>
        <button
          onClick={syncNow}
          className="ml-2 underline underline-offset-2 hover:no-underline font-semibold"
        >
          Réessayer
        </button>
      </div>
    ),
    success: (
      <div className="flex items-center justify-center gap-2 py-2 bg-emerald-500/90 text-white text-sm font-medium">
        <CheckCircle2 className="w-4 h-4 shrink-0" />
        <span>Données synchronisées</span>
      </div>
    ),
  };

  return (
    <AnimatePresence mode="wait">
      {state !== 'idle' && (
        <motion.div
          key={state}
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.2 }}
          className="overflow-hidden"
        >
          {banners[state]}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
